import { defineStore } from 'pinia';
import axios from 'axios';
import { useMainStore } from '@/stores/useMainStore';
import { useAppStore } from '@/stores/useAppStore';
import { reportFatalError, reportNonFatalError, reportSilentError } from '@/utils/storeErrorReporting';
import { getAccessDeniedError, getStudentAccessDeniedError, getAuthFailedError, getUserDataError, getUserFetchError } from '@/utils/errorMessages';
import { roles, API_DOMAIN } from '@/constants';
import * as educatorApi from '@/api/educators';
import { hasAcceptedAgreements, hasCompletedOnboarding } from '@/utils/auth/agreements';

/**
 * Check Career Connect access via product settings API
 * @param {string} accessToken - Bearer token for authentication
 * @returns {Promise<boolean>} True if user has CARCON_ENABLED setting
 */
const checkAccessViaProductSettings = async (accessToken) => {
    try {
        const response = await axios.get(`${API_DOMAIN}/deproxy/v1/users/product_settings`, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });

        return response.data.settings.some((setting) => setting.code === 'CARCON_ENABLED' && setting.value === true);
    } catch {
        return false;
    }
};

export const useEducatorStore = defineStore('educator', {
    state: () => ({}),

    getters: {
        /**
         * Get educator data from main store when in educator mode
         * @returns {Object} Educator profile data
         */
        educatorData() {
            const mainStore = useMainStore();
            const appStore = useAppStore();
            if (appStore.mode === roles.EDUCATOR_ROLE) {
                return mainStore.user.data;
            }
            return {};
        },

        /**
         * Check if educator is active
         * @returns {boolean} True if educator is active
         */
        isEducatorActive() {
            const mainStore = useMainStore();
            const appStore = useAppStore();
            if (appStore.mode === roles.EDUCATOR_ROLE) {
                return hasCompletedOnboarding(mainStore.user.data);
            }
            return false;
        },
    },

    actions: {
        /**
         * Store educator data in main store
         * @param {Object} data - Educator data from API
         * @param {boolean} view - Whether this is for page view vs current user
         */
        async storeEducatorData(data, view) {
            const mainStore = useMainStore();
            const appStore = useAppStore();

            if (!data) {
                await appStore.updateApp({ active: false });
                await mainStore.updateUserData({ notCreated: true });
                return;
            }

            const { isActive, termsChecked, privacyChecked, codeOfConductChecked, educatorId: id, preferredName, avatarUrl } = data;

            if (view) {
                mainStore.updatePage({
                    educator: {
                        isActive,
                        termsChecked,
                        privacyChecked,
                        codeOfConductChecked,
                        id,
                        preferredName,
                        avatarUrl,
                        notCreated: false,
                    },
                });
                return;
            }

            await appStore.updateApp({ active: hasCompletedOnboarding(data) });

            mainStore.updateUserData({
                isActive,
                termsChecked,
                privacyChecked,
                codeOfConductChecked,
                id,
                preferredName,
                avatarUrl,
                notCreated: false,
            });
        },

        /**
         * Get DE (Discovery Education) user data
         * @param {Object} payload - Contains token for authentication
         * @param {string} payload.token - Bearer token
         * @returns {Promise<Object|null>} User data or null if error
         */
        async getDEUser(payload) {
            const accessToken = payload.token;
            let response;

            const mainStore = useMainStore();

            try {
                response = await axios.get(`${API_DOMAIN}/deproxy/v1/users/me?return_avatar=true`, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });
            } catch (error) {
                reportFatalError(error, getAuthFailedError());
                // Don't set active to true on error!
                return null;
            }

            // Validate response structure before destructuring
            if (!response?.data?.user) {
                const error = new Error('Invalid API response structure');
                reportFatalError(error, getUserDataError());
                return null;
            }

            const {
                id,
                first_name: firstName,
                last_name: lastName,
                site,
                email,
                products,
                timezone,
                avatar: avatarUrl,
                roles: userRoles,
            } = response.data.user;

            const isStudent = userRoles && userRoles.some((role) => role.code === 'STUDENT');
            if (isStudent) {
                reportFatalError(new Error('Student role not allowed'), getStudentAccessDeniedError());
                return null;
            }

            const hasAccessViaProduct = !!products.find((product) => product.code === 'CARCON');

            if (!hasAccessViaProduct) {
                const hasAccessViaSettings = await checkAccessViaProductSettings(accessToken);
                const hasDEXAccess = !!products.find((product) => product.code === 'US');

                if (!hasAccessViaSettings || !hasDEXAccess) {
                    reportFatalError(new Error('Career Connect access not provisioned'), getAccessDeniedError());
                    return null;
                }
            }

            return mainStore.updateUserData({
                id,
                firstName,
                lastName,
                site,
                email,
                timezone,
                avatarUrl,
                roles: userRoles,
            });
        },

        /**
         * Get site/school location data
         * @param {Object} payload - Contains siteId and token
         * @param {string} payload.siteId - Site ID to fetch
         * @param {string} payload.token - Bearer token
         * @returns {Promise<Object|null>} Site data or null if error
         */
        async getSite(payload) {
            const { siteId, token } = payload;
            const mainStore = useMainStore();
            let site;

            try {
                const response = await axios.get(`${API_DOMAIN}/deproxy/v1/sites/${siteId}/location`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                site = response.data.site;
            } catch (error) {
                reportSilentError(error);
            }

            if (site) {
                return mainStore.updateUserData({
                    site,
                });
            }
            return null;
        },

        /**
         * Get educator profile data
         * @param {string} payload.data.educatorId - Optional educator ID
         * @param {boolean} payload.data.view - true: if user is viewing an Educators profile, false/null: fetching and storing Educator metadata
         */
        async getEducator(payload) {
            const mainStore = useMainStore();
            const { view, educatorId } = (payload && payload.data) || {};

            try {
                mainStore.updateLoading({ key: 'user', status: true });

                const resolvedEducatorId = educatorId || mainStore.user.data?.id;
                const result = resolvedEducatorId ? await educatorApi.fetchEducatorFromId(resolvedEducatorId) : undefined;

                if (!result) {
                    mainStore.updateLoading({ key: 'user', status: false });
                    return;
                }

                const { avatarUrl: deAvatar } = mainStore.user.data;
                const { avatarUrl: ccAvatar } = result;

                const avatarUrl = deAvatar || ccAvatar;

                const data = {
                    ...result,
                    avatarUrl,
                };

                const avatarMismatch = deAvatar && ccAvatar && deAvatar.trim() !== ccAvatar.trim();
                const noStoredAvatar = deAvatar && !ccAvatar;

                await this.storeEducatorData(data, view);

                if (noStoredAvatar || avatarMismatch) {
                    await this.updateEducator({ token: payload.token });
                }

                if (!result || !result?.timezone) {
                    const { timezone } = mainStore.user.data;
                    await this.updateEducator({ token: payload.token, data: { ...result, timezone } });
                }

                mainStore.updateLoading({ key: 'user', status: false });
            } catch (error) {
                reportSilentError(error);
                mainStore.updateLoading({ key: 'user', status: false });
            }
        },

        /**
         * Create educator profile
         * @param {Object} payload - Contains token and educator data
         * @param {string} payload.token - Bearer token
         * @param {Object} payload.data - Educator data to create
         * @param {string} payload.data.id - Educator ID
         * @returns {Promise<Object|null>} Created educator data or null on error
         */
        async createEducator(payload) {
            const { token, data } = payload;

            try {
                const response = await axios.post(`${API_DOMAIN}/educators/${data.id}`, data, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                return response.data;
            } catch (error) {
                reportSilentError(error);
                return null;
            }
        },

        /**
         * Update educator profile
         * @param {Object} payload - Contains token and optional data
         * @param {string} payload.token - Bearer token
         * @param {Object} payload.data - Optional update data
         * @param {boolean} payload.data.activate - Whether to activate educator
         */
        async updateEducator(payload) {
            const { token, data } = payload;
            const mainStore = useMainStore();

            const { ...userData } = mainStore.user.data;

            if (data) {
                Object.assign(userData, data);
            }

            if (data && data.activate) {
                // The BE does not enforce agreements, so never send an active record without them.
                if (!hasAcceptedAgreements(userData)) {
                    reportSilentError(new Error('Blocked educator activation: an agreement has not been accepted'));
                    return;
                }
                userData.isActive = true;
            }

            try {
                mainStore.updateLoading({ key: 'user', status: true });

                const response = await axios.post(`${API_DOMAIN}/educators/${userData.id}`, userData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                await this.storeEducatorData(response.data, false);
                mainStore.updateLoading({ key: 'user', status: false });
            } catch (error) {
                reportNonFatalError(error, getUserFetchError());
                mainStore.updateLoading({ key: 'user', status: false });
            }
        },

        /**
         * Update educator avatar
         * @param {Object} payload - Contains token and file content
         * @param {string} payload.token - Bearer token
         * @param {Object} payload.data - Avatar data
         * @param {string} payload.data.fileContent - Base64 encoded avatar image
         */
        async updateEducatorAvatar(payload) {
            const mainStore = useMainStore();

            try {
                const response = await axios.post(`${API_DOMAIN}/educators/${mainStore.user.data.id}/update-avatar`, payload.data.fileContent, {
                    headers: {
                        Authorization: `Bearer ${payload.token}`,
                        'Content-Type': 'application/json',
                    },
                });

                await this.storeEducatorData(response.data, false);
            } catch (error) {
                reportSilentError(error);
            }
        },
    },
});
